import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, PenLine, CheckCircle2, Building2, Wallet, Users, ExternalLink, ShieldCheck, TrendingUp, XCircle, AlertTriangle, Award, Lock, BookOpen, X } from "lucide-react";
import { api, API } from "../lib/api";

const DURATION = { first_payment: "First payment only", "12_months": "First 12 months", lifetime: "Lifetime of client" };
const EXCL = { exclusive: "Exclusive referrals", rofr: "Right of first refusal", non_circumvention: "Non-circumvention" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const PAYOUT_LABEL = { paid: "Paid", completed: "Paid", pending_no_account: "Pending (connect bank)", failed: "Failed" };
const REF_STATUS_LABEL = { referred: "Referred", signed: "Signed", active: "Active", paid: "Paid", unpaid: "Unpaid", dropped: "Dropped", chargeback: "Chargeback" };
const ROLE_LABEL = { referrer: "Referrer", sales: "Sales Partner", embedded: "Embedded" };
const REF_STATUS_COLORS = {
  referred: "bg-[#6b6b6b]/10 text-[#6b6b6b]", signed: "bg-[#4d5a24]/15 text-[#4d5a24]",
  active: "bg-[#1e7d5a]/15 text-[#1e7d5a]", paid: "bg-[#c9a961]/20 text-[#8a6d2f]",
  unpaid: "bg-[#c98a3f]/15 text-[#a3823f]", dropped: "bg-[#6b6b6b]/15 text-[#6b6b6b]",
  chargeback: "bg-[#b23a48]/15 text-[#b23a48]",
};

export default function PartnerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [statement, setStatement] = useState(null);
  const [showStatement, setShowStatement] = useState(false);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [loadingAgreement, setLoadingAgreement] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/partner-portal/${token}`); setData(data); setName(data.signatory_name || data.partner_name || ""); }
    catch { setErr(true); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openAgreement = async () => {
    setShowAgreement(true);
    if (agreement) return;
    setLoadingAgreement(true);
    try { const { data } = await api.get(`/partner-portal/${token}/agreement`); setAgreement(data); }
    catch { toast.error("Could not load the agreement."); setShowAgreement(false); }
    finally { setLoadingAgreement(false); }
  };

  const openStatement = async () => {
    setShowStatement(true);
    setLoadingStatement(true);
    try { const { data } = await api.get(`/partner-portal/${token}/statement`); setStatement(data); }
    catch { toast.error("Could not load the statement."); setShowStatement(false); }
    finally { setLoadingStatement(false); }
  };

  const sign = async () => {
    if (!name.trim() || !agree) return toast.error("Type your full legal name and check the box to sign.");
    setSigning(true);
    try { await api.post(`/partner-portal/${token}/sign`, { signature_name: name.trim(), agree: true }); toast.success("Agreement signed — thank you!"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not sign."); }
    finally { setSigning(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try { const { data } = await api.post(`/partner-portal/${token}/connect/onboard`); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Stripe onboarding."); setConnecting(false); }
  };

  if (err) return <Centered><p className="text-[#1e3d3a]/70" data-testid="portal-error">This link is invalid or has expired. Please ask Elevare for a fresh link.</p></Centered>;
  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin text-[#c9a961]" /></Centered>;

  if (data.access_suspended) {
    return (
      <Centered>
        <div data-testid="partner-portal-locked" className="max-w-md">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#b23a48]/12"><Lock className="h-6 w-6 text-[#b23a48]" /></div>
          <h1 className="text-2xl font-semibold text-[#1e3d3a]">Portal access paused</h1>
          <p className="mt-3 text-sm text-[#1e3d3a]/65">Your access to this portal is currently suspended. Per your agreement, access is restored once your account is brought current. Please contact Elevare Solutions to reinstate it.</p>
          <a href="mailto:hello@elevaresolutions.com?subject=Restore%20my%20partner%20portal%20access" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-5 py-2.5 text-xs font-semibold text-[#f4f1e8] hover:bg-[#12302c]">Contact Elevare</a>
        </div>
      </Centered>
    );
  }

  const s = data.summary || {};
  const signed = data.status === "signed";

  return (
    <div className="min-h-screen bg-[#f4f1e8]" data-testid="partner-portal">
      <header className="bg-[#1e3d3a] text-[#f4f1e8] px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="text-lg tracking-[0.14em] text-[#c9a961]">ELEVARE SOLUTIONS</div>
          <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full ${signed ? "bg-[#c9a961] text-[#1e3d3a]" : "bg-white/10 text-[#f4f1e8]"}`}>{signed ? "Signed" : (data.status || "draft")}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#a3823f]">Referral Partner Portal</p>
          <h1 className="text-3xl font-semibold text-[#1e3d3a] mt-1">{data.partner_name}</h1>
          {signed && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1 text-[11px] font-semibold text-[#c9a961]" data-testid="portal-tier-badge">
              <Award className="h-3.5 w-3.5" /> Current tier {Number(data.current_tier_pct ?? data.terms?.revenue_pct)}% · {s.clients_signed || 0} client{(s.clients_signed || 0) === 1 ? "" : "s"} signed
            </div>
          )}
        </div>

        {/* Agreement / sign */}
        <section className="bg-white rounded-2xl border border-[#1e3d3a]/10 p-6" data-testid="portal-agreement">
          <div className="flex items-center gap-2 text-[#1e3d3a] font-semibold"><FileText className="h-4 w-4 text-[#c9a961]" /> Your agreement</div>
          <button onClick={openAgreement} data-testid="portal-view-agreement" className="inline-flex items-center gap-1.5 mt-3 text-sm text-[#a3823f] hover:underline">Review the full agreement <BookOpen className="h-3.5 w-3.5" /></button>
          <button onClick={openStatement} data-testid="portal-statement" className="inline-flex items-center gap-1.5 mt-3 ml-4 text-sm text-[#a3823f] hover:underline">View commission statement <BookOpen className="h-3.5 w-3.5" /></button>

          {signed ? (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#4d5a24]/10 px-4 py-3 text-sm text-[#3c4a1c]" data-testid="portal-signed">
              <CheckCircle2 className="h-4 w-4" /> Signed by <b>{data.signature_name}</b>{data.signed_at ? ` on ${new Date(data.signed_at).toLocaleDateString()}` : ""}.
            </div>
          ) : (
            <div className="mt-4 space-y-3 border-t border-[#1e3d3a]/8 pt-4">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 block">Type your full legal name to sign</label>
              <input data-testid="portal-sign-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none" />
              <label className="flex items-start gap-2 text-xs text-[#1e3d3a]/70">
                <input data-testid="portal-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
                I have read and agree to be legally bound by this agreement (E-SIGN electronic signature).
              </label>
              <button data-testid="portal-sign-btn" onClick={sign} disabled={signing} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-5 py-2.5 text-xs font-semibold text-[#f4f1e8] hover:bg-[#12302c] disabled:opacity-50">{signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Sign agreement</button>
            </div>
          )}
        </section>

        {/* Terms */}
        <section className="bg-white rounded-2xl border border-[#1e3d3a]/10 p-6" data-testid="portal-terms">
          <div className="text-[#1e3d3a] font-semibold mb-3">Your terms</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Stat label="Commission" value={`${data.terms?.revenue_pct}%`} />
            <Stat label="On revenue for" value={DURATION[data.terms?.commission_duration] || "—"} />
            <Stat label="Flat fee / client" value={money(data.terms?.flat_fee_per_client)} />
            <Stat label="Exclusivity" value={EXCL[data.terms?.exclusivity_mode] || "—"} />
          </div>
        </section>

        {/* Commissions & performance */}
        <section className="bg-white rounded-2xl border border-[#1e3d3a]/10 p-6" data-testid="portal-commissions">
          <div className="flex items-center gap-2 text-[#1e3d3a] font-semibold mb-3"><Wallet className="h-4 w-4 text-[#c9a961]" /> Commissions & performance</div>
          {s.mrr > 0 && (
            <div className="mb-4 flex items-center justify-between rounded-xl bg-[#1e3d3a] px-4 py-3 text-white" data-testid="portal-mrr">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#c9a961]"><TrendingUp className="h-3.5 w-3.5" /> Monthly recurring commission</div>
              <div className="text-xl font-semibold tabular-nums text-[#c9a961]">{money(s.mrr)}<span className="text-xs font-normal text-white/50">/mo</span></div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <BigStat label="Earned" value={money(s.commission_earned)} />
            <BigStat label="Expected future" value={money(s.expected_future)} icon={<TrendingUp className="h-3.5 w-3.5" />} />
            <BigStat label="Paid out" value={money(s.commission_paid)} />
            <BigStat label="Owed to you" value={money(s.commission_owed)} accent />
          </div>

          {/* Client lifecycle breakdown */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="portal-lifecycle">
            <MiniStat label="Clients signed" value={s.clients_signed || 0} tone="teal" />
            <MiniStat label="Paid" value={s.paid_count || 0} tone="gold" />
            <MiniStat label="Unpaid" value={s.unpaid_count || 0} tone="amber" />
            <MiniStat label="Dropped" value={s.dropped_count || 0} sub={s.dropped_potential > 0 ? `${money0(s.dropped_potential)} lost` : null} tone="muted" icon={<XCircle className="h-3 w-3" />} />
            <MiniStat label="Chargebacks" value={s.chargeback_count || 0} sub={s.chargeback_amount > 0 ? `−${money0(s.chargeback_amount)}` : null} tone="red" icon={<AlertTriangle className="h-3 w-3" />} />
          </div>

          <div className="mt-5 border-t border-[#1e3d3a]/8 pt-4">
            {data.connect?.payouts_enabled ? (
              <div className="flex items-center gap-2 text-sm text-[#3c4a1c]" data-testid="portal-connect-ready"><ShieldCheck className="h-4 w-4" /> Your bank is connected for automatic Stripe payouts.</div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[#1e3d3a]/70">Connect a bank account to receive your commissions via Stripe.</p>
                <button data-testid="portal-connect-btn" onClick={connect} disabled={connecting} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-xs font-medium text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50"><Building2 className="h-3.5 w-3.5" /> {data.connect?.started ? "Finish bank setup" : "Connect bank"}</button>
              </div>
            )}
          </div>

          {(data.payouts || []).length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="portal-payouts">
              {data.payouts.map((x, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-[#1e3d3a]/70 border-b border-[#1e3d3a]/5 py-1.5">
                  <span>{new Date(x.created_at).toLocaleDateString()} · {x.method || "—"}</span>
                  <span className="tabular-nums font-medium text-[#1e3d3a]">{money(x.amount)} <span className="text-[#a3823f]">· {PAYOUT_LABEL[x.status] || x.status}</span></span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Referrals */}
        <section className="bg-white rounded-2xl border border-[#1e3d3a]/10 p-6" data-testid="portal-referrals">
          <div className="flex items-center gap-2 text-[#1e3d3a] font-semibold mb-3"><Users className="h-4 w-4 text-[#c9a961]" /> Your referrals ({(data.referrals || []).length})</div>
          {(data.referrals || []).length === 0 ? (
            <p className="text-sm text-[#1e3d3a]/50">No referrals logged yet.</p>
          ) : (
            <div className="space-y-1.5">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-[#1e3d3a]/5 py-2" data-testid={`portal-referral-${i}`}>
                  <div className="min-w-0">
                    <div className="text-[#1e3d3a] flex items-center gap-2">
                      <span className="truncate">{r.client_name}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${REF_STATUS_COLORS[r.status] || REF_STATUS_COLORS.referred}`}>{REF_STATUS_LABEL[r.status] || r.status}</span>
                      {r.recurring && <span className="shrink-0 rounded-full bg-[#1e3d3a]/8 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#1e3d3a]/60">{ROLE_LABEL[r.role] || r.role}</span>}
                    </div>
                    <div className="text-[11px] text-[#1e3d3a]/45">
                      {r.payout_type === "lump"
                        ? `Lump sum`
                        : r.recurring
                          ? `${money(r.monthly_commission)}/mo · ${money(r.monthly_payment)} client payment · ${r.months_paid || 0} mo paid`
                          : `${r.commission_pct}% of ${money(r.revenue_to_date)}${r.basis === "client_sales" ? " sales" : ""}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-semibold text-[#a3823f]">{money(r.payout)}</div>
                    <div className="text-[10px] text-[#1e3d3a]/40 tabular-nums">{r.recurring ? "projected" : `${money(r.revenue_to_date)} deal`}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        <p className="text-center text-[11px] text-[#1e3d3a]/40 pt-2">Elevare — Latin, "to elevate."</p>
      </main>

      {showAgreement && (
        <div data-testid="agreement-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAgreement(false); }}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-[#f4f1e8] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#1e3d3a]/10 px-6 py-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">View only · not downloadable</p>
                <h3 className="truncate text-lg font-semibold text-[#1e3d3a]">{agreement?.title || "Referral Agreement"}</h3>
              </div>
              <button data-testid="agreement-close" onClick={() => setShowAgreement(false)} className="rounded-full p-1.5 text-[#1e3d3a]/50 hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto px-6 py-5 select-none" onCopy={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}>
              {loadingAgreement || !agreement ? (
                <div className="flex items-center gap-2 py-10 text-[#1e3d3a]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading agreement…</div>
              ) : (
                agreement.sections.map((sec, i) => (
                  <div key={i} className="mb-5">
                    <h4 className="mb-1.5 text-sm font-semibold text-[#1e3d3a]">{sec.heading}</h4>
                    {sec.paragraphs.map((para, j) => (
                      <p key={j} className="mb-2 text-[13px] leading-relaxed text-[#1e3d3a]/80" dangerouslySetInnerHTML={{ __html: para }} />
                    ))}
                  </div>
                ))
              )}
            </div>
            <div className="border-t border-[#1e3d3a]/10 px-6 py-3 text-center text-[11px] text-[#1e3d3a]/45">This agreement is view-only. Contact Elevare Solutions for any questions before signing.</div>
          </div>
        </div>
      )}

      {showStatement && (
        <div data-testid="statement-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowStatement(false); }}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-[#f4f1e8] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#1e3d3a]/10 px-6 py-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">View only · not downloadable</p>
                <h3 className="truncate text-lg font-semibold text-[#1e3d3a]">Commission Statement</h3>
              </div>
              <button data-testid="statement-close" onClick={() => setShowStatement(false)} className="rounded-full p-1.5 text-[#1e3d3a]/50 hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto px-6 py-5 select-none" onCopy={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}>
              {loadingStatement || !statement ? (
                <div className="flex items-center gap-2 py-10 text-[#1e3d3a]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading statement…</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {[["Earned", statement.summary.earned], ["Paid", statement.summary.paid], ["Owed", statement.summary.owed]].map(([l, v]) => (
                      <div key={l} className="rounded-xl bg-white border border-[#1e3d3a]/10 px-3 py-3 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/45">{l}</div>
                        <div className="text-lg font-semibold text-[#1e3d3a]">${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm font-semibold text-[#1e3d3a] mb-2">Deals</div>
                  <div className="overflow-hidden rounded-xl border border-[#1e3d3a]/10">
                    <table className="w-full text-[13px]">
                      <thead className="bg-[#1e3d3a] text-[#f4f1e8]"><tr>
                        <th className="px-3 py-2 text-left font-medium">Client</th><th className="px-3 py-2 text-right font-medium">Base</th>
                        <th className="px-3 py-2 text-left font-medium">Rate</th><th className="px-3 py-2 text-right font-medium">Payout</th><th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr></thead>
                      <tbody>
                        {statement.deals.length === 0 ? <tr><td colSpan={5} className="px-3 py-3 text-[#1e3d3a]/50">No deals logged yet.</td></tr> :
                          statement.deals.map((d, i) => (
                            <tr key={i} className="border-t border-[#1e3d3a]/8 text-[#1e3d3a]/80">
                              <td className="px-3 py-2">{d.client_name}</td><td className="px-3 py-2 text-right">${Number(d.base).toLocaleString()}</td>
                              <td className="px-3 py-2">{d.rate}</td><td className="px-3 py-2 text-right">${Number(d.payout).toLocaleString()}</td><td className="px-3 py-2 capitalize">{d.status}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="border-t border-[#1e3d3a]/10 px-6 py-3 text-center text-[11px] text-[#1e3d3a]/45">This statement is view-only and reflects amounts recorded to date.</div>
          </div>
        </div>
      )}
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-screen bg-[#f4f1e8] flex items-center justify-center px-6 text-center">{children}</div>;
const Stat = ({ label, value }) => (<div><div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">{label}</div><div className="text-[#1e3d3a] font-medium mt-0.5">{value}</div></div>);
const BigStat = ({ label, value, accent, icon }) => (<div className={`rounded-xl px-3 py-3 ${accent ? "bg-[#c9a961]/12" : "bg-[#1e3d3a]/5"}`}><div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 flex items-center gap-1">{icon}{label}</div><div className={`text-lg font-semibold mt-1 tabular-nums ${accent ? "text-[#a3823f]" : "text-[#1e3d3a]"}`}>{value}</div></div>);
const MINI_TONE = {
  teal: "border-[#1e3d3a]/15 text-[#1e3d3a]", gold: "border-[#c9a961]/40 text-[#8a6d2f]",
  amber: "border-[#c98a3f]/30 text-[#a3823f]", muted: "border-[#6b6b6b]/20 text-[#6b6b6b]",
  red: "border-[#b23a48]/30 text-[#b23a48]",
};
const MiniStat = ({ label, value, sub, tone = "teal", icon }) => (
  <div className={`rounded-xl border bg-white px-3 py-2.5 ${MINI_TONE[tone] || MINI_TONE.teal}`}>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 flex items-center gap-1">{icon}{label}</div>
    <div className="text-xl font-semibold mt-0.5 tabular-nums">{value}</div>
    {sub && <div className="text-[10px] mt-0.5 opacity-80">{sub}</div>}
  </div>
);
